import { motion, MotionValue, useTransform } from "framer-motion";
import { I18n } from "@assets/strings";
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { useHistory } from "@core/routing/useHistory";
import { ClipLoader } from "react-spinners";
import styled, { css, useTheme } from "styled-components";
import { Path, searchParams } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { useAsync, useIsMobile, useScrollDelta, useWindowSize } from "@utils/hooks";
import { MessagingMenu } from "@ui/pages/messagingMenu";
import { gap, mobile, smallMobile } from "@ui/styles/utils";
import { Z } from "@ui/styles/zIndex";
import { ChevronDownIcon, CreateIcon, NavHamburgerIcon } from "@ui/components/icons";
import { Menu } from "@ui/components/menus/menu";
import { NotificationMenu } from "@ui/components/notification/notificationMenu";
import { ProfilePicture } from "@ui/components/profile/profilePicture";
import { SearchBar } from "@ui/components/search/searchBar";
import { CreationMenuContent } from "./creationMenuContent";
import { ProfileMenuContent } from "./profileMenuContent";
import { TopBarButtonLabel, TopBarDesktopRoutes } from "./topBarNavItems";
import { TopBarMobileMenuContent } from "./topBarMobileMenuContent";
import { useTopBarNavigation } from "./useTopBarNavigation";
// import { useMessagingControllerContext } from "../../../domain/messaging/messagingControllerContext";
import { Event } from "stream-chat";
import logoHeader from "@assets/images/variant-jaangal/logo-header.png";
import { config } from "@core/config/config";
import { Environment } from "@core/config/environment";
import { useConnectUser } from "@utils/messaging/hooks";
import { row } from "@ui/components/layout";

interface TopBarProps {
	isBannerOnTop?: boolean;
}

export const TopBar: React.FC<TopBarProps> = ({ isBannerOnTop }) => {
	const MAX_MOBILE_WIDTH = 900;
	const history = useHistory();
	const location = useLocation();
	const isMobile = useIsMobile();

	const delta = useScrollDelta(90);
	const offset = useTransform(delta, (v) => -v);
	const [barOffset, setBarOffset] = useState<MotionValue<number> | number>(offset);
	const { width } = useWindowSize();
	const { postService, notificationService } = useServices();
	const currentProfile = useCurrentProfile();
	const theme = useTheme();

	const connectUser = useConnectUser({
		profileId: currentProfile?.id,
		userToken: currentProfile?.chatToken?.token,
	});

	const [query, setQuery] = useState("");
	const debouncedChange = useCallback(
		debounce((v) => setQuery(v), 500, { leading: true, trailing: true }),
		[]
	);
	const { run: refresh, loading: refreshing } = useAsync(async () => {
		await postService.fetchFeed();
		await notificationService.fetchNotifications();
	}, []);

	useEffect(() => {
		if (width > MAX_MOBILE_WIDTH) {
			setBarOffset(0);
		} else {
			setBarOffset(offset);
		}
	}, [width]);

	const [fixedMenuOpened, setFixedMenuOpened] = useState({
		notifications: false,
		profile: false,
		creation: false,
		navigation: false,
	});

	useEffect(() => {
		const handleNewMessage = (event: Event): void => {
			setUnreadMessages(event.unread_channels as number);
		};

		connectUser?.chatClient.on("message.new", handleNewMessage);

		return () => {
			connectUser?.chatClient.off("message.new", handleNewMessage);
		};
	}, [connectUser?.chatClient]);

	useEffect(() => {
		const handleReadMessage = (event: Event): void => {
			if (event?.user?.id === currentProfile?.id) {
				setUnreadMessages((prev) => prev - 1);
			}
		};
		connectUser?.chatClient.on("message.read", handleReadMessage);

		return () => {
			connectUser?.chatClient.off("message.read", handleReadMessage);
		};
	}, [connectUser?.chatClient, currentProfile]);

	const [unreadMessages, setUnreadMessages] = useState(0);
	const navigation = useTopBarNavigation();

	// if (isMobile && !visibleOnMobile) {
	// 	return null;
	// }

	if (!currentProfile) {
		return (
			<Container style={isMobile ? { y: barOffset } : undefined}>
				<HomeButton
					onClick={() => {
						if (location.pathname === Path.Root) {
							refresh();
							window.scrollTo(0, 0);
						} else {
							history.push(Path.Root);
						}
					}}
				>
					<img src={logoHeader} alt="Jaangal By Tooshare" />
				</HomeButton>
			</Container>
		);
	}

	const barHiddenOnScroll =
		isMobile && !fixedMenuOpened.notifications && !fixedMenuOpened.profile && !fixedMenuOpened.navigation;

	return (
		<Container $isBannerOnTop={isBannerOnTop} style={barHiddenOnScroll ? { y: barOffset } : undefined}>
			{refreshing && (
				<SpinnerWrapper>
					<ClipLoader color={theme.colors.secondary} />
				</SpinnerWrapper>
			)}
			<Left>
				{isMobile && (
					<Menu
						bottomSheetMode
						bottomSheetHeight={600}
						closeOnSelect
						onOpenChange={(open) => setFixedMenuOpened((current) => ({ ...current, navigation: open }))}
						renderMenuContent={(close) => (
							<TopBarMobileMenuContent onClose={close} unreadMessages={unreadMessages} profileId={currentProfile.id} />
						)}
					>
						<Button selected={fixedMenuOpened.navigation}>
							<NavHamburgerIcon />
						</Button>
					</Menu>
				)}
				<HomeButton
					onClick={() => {
						if (location.pathname === Path.Root) {
							refresh();
							window.scrollTo(0, 0);
						} else {
							history.push(Path.Root);
						}
					}}
				>
					<img src={logoHeader} alt="Jaangal By Tooshare" />
				</HomeButton>
				{!isMobile && (
					<SearchBarWrapper id="search-bar">
						<SearchBar
							value={query}
							onChange={debouncedChange}
							onSubmit={(value) =>
								history.push({
									pathname: Path.Search,
									search: searchParams(value),
								})
							}
							placeholder={I18n.t("search.input.placeholder")}
							absoluteAutocompletionActive
						/>
					</SearchBarWrapper>
				)}
			</Left>
			<div>
				{!isMobile && (
					<>
						<Routes>
							<TopBarDesktopRoutes
								navigation={navigation}
								Button={Button}
								ButtonLabel={TopBarButtonLabel}
								creationMenu={
									<Menu
										fixed="center"
										onOpenChange={(open) => setFixedMenuOpened((current) => ({ ...current, creation: open }))}
										closeOnSelect
										renderMenuContent={() => <CreationMenuContent />}
									>
										<Button selected={fixedMenuOpened.creation}>
											<CreateIcon />
											<TopBarButtonLabel selected={fixedMenuOpened.creation}>{I18n.t("create")}</TopBarButtonLabel>
										</Button>
									</Menu>
								}
							/>
						</Routes>
						<Separator />
					</>
				)}
				<Right>
					{!isMobile && config.features.messaging.showInTopBar && connectUser?.chatClient && (
						<MessagingMenu
							chatClient={connectUser?.chatClient}
							unreadMessages={unreadMessages}
							profileId={currentProfile.id}
						/>
					)}
					<NotificationMenu
						onChange={(open) => setFixedMenuOpened((current) => ({ ...current, notifications: open }))}
					/>
					<Menu
						fixed="right"
						renderMenuContent={() => <ProfileMenuContent />}
						menuContentStyle={{ padding: 0 }}
						closeOnSelect
						onOpenChange={(open) => setFixedMenuOpened((current) => ({ ...current, profile: open }))}
					>
						<ProfileMenu opened={fixedMenuOpened.profile}>
							<ProfilePicture size={"M"} picture={currentProfile?.avatar} />
							<ChevronDownIcon />
						</ProfileMenu>
					</Menu>
				</Right>
			</div>
		</Container>
	);
};

const Container = styled(motion.div)<{ $isBannerOnTop?: boolean }>`
	position: fixed !important;
	
	${({ $isBannerOnTop }) =>
		$isBannerOnTop
			? css`
					top: 50px;
				`
			: css`
					top: 0;
				`}
	left: 0  !important;
	right: 0 !important;
	background-color: ${({ theme }) => theme.colors.backgroundPrimary} !important;
	box-shadow: ${
		Environment.APP_VARIANT === "jaangal"
			? "0 2px 34px 0 rgba(0, 0, 0, 0.04) !important"
			: "0 2px 34px 0 rgba(0, 0, 0, 0.2) !important"
	};
	z-index: ${Z.toolbar} !important;
	${row(20, "space-between", "center")};
	padding: 9px 70px;
	@media (max-width: 1000px) {
		padding: 9px 20px;
	}
	@media ${smallMobile} {
		padding: 9px 10px;
	}
	@media ${mobile} {
		${row(10, "space-between", "center")};
		${({ $isBannerOnTop }) =>
			$isBannerOnTop
				? css`
						top: 50px;
					`
				: css`
						top: 0;
					`};
	}
	> div {
		display: flex;
		align-items: center;
	}
`;

const HomeButton = styled.div`
	cursor: pointer;
	display: flex;
	flex-direction: column;
	justify-content: center;
	width: 214px;
	height: 36px;

	@media (max-width: 900px) {
		width: 160px;
		height: 27px;
	}
	img {
		width: 100%;
		height: 100%;
		object-fit: contain;
		object-position: left center;
	}
`;

const SearchBarWrapper = styled.div`
	flex-grow: 1;
`;

const Left = styled.div`
	${gap(45, "row")};
	flex-grow: 1;
	display: flex;
	@media (max-width: 900px) {
		${gap(10, "row")};
		flex-grow: 0;
	}
`;

const Right = styled.div`
	${gap(15, "row")};
	display: flex;
	flex-direction: row;
	align-items: center;
`;

const Routes = styled.div`
	display: flex;
	@media (min-width: 900px) {
		padding: 0 12px;
	}
`;

const mainActionStyle = css<{ selected: boolean }>`
	padding: 15px;
	@media (max-width: 900px) {
		padding: 12px;
	}

	border-radius: 11px;
	transition: background-color 0.2s linear;
	&:hover {
		background-color: ${({ theme }) => theme.colors.lightergray};
	}
	> svg {
		width: 24px;
		height: 24px;

		path {
			transition: fill 0.2s linear;
			fill: ${({ selected, theme }) => (selected ? theme.colors.primary : theme.colors.textPrimary)};
		}
	}
	@media ${smallMobile} {
		padding: 12px 6px;
		> svg {
			/* width: 16px;
			height: 16px; */
		}
	}
`;

const Button = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "selected",
})<{ selected: boolean }>`
	cursor: pointer;
	${mainActionStyle};
	${row(6, "flex-start", "center")};
`;

const Separator = styled.div`
	width: 1px;
	height: 50px;
	background-color: ${({ theme }) => theme.colors.lightergray};
	margin-right: 36px;
	@media (max-width: 900px) {
		margin-right: 16px;
	}
	@media ${smallMobile} {
		margin-right: 6px;
	}
`;

const ProfileMenu = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "opened",
})<{ opened: boolean }>`
	display: flex;
	align-items: center;
	${gap(6, "row")};

	svg {
		flex-shrink: 0;
		transition: transform ease-in-out 0.2s;
		will-change: transform;
		${({ opened }) => opened && "transform: rotate(180deg)"};
	}
`;

const SpinnerWrapper = styled.div`
	width: 60px;
	height: 60px;
	border-radius: 30px;
	box-shadow: 0 4px 46px 0 rgba(0, 0, 0, 0.2);
	${row(0, "center", "center")}
	position: absolute;
	top: 120px;
	left: 50%;
	transform: translateX(-50%);
`;
